import { useEffect, useState } from 'react';
import { FileText, Image as ImageIcon, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const STATUS_CHIP = {
    submitted: 'bg-amber-100 text-amber-700',
    approved:  'bg-blue-100 text-blue-700',
    paid:      'bg-emerald-100 text-emerald-700',
    rejected:  'bg-rose-100 text-rose-700',
    cancelled: 'bg-ink-100 text-ink-700',
};

export default function AdminReimbursements() {
    const [items, setItems] = useState([]);
    const [filters, setFilters] = useState({ status: '' });
    const [picked, setPicked] = useState(null);

    const load = async () => {
        const params = filters.status ? { status: filters.status } : {};
        const { data } = await api.get('/reimbursements/admin', { params });
        setItems(data.items);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status]);

    const review = async (r, status) => {
        let reviewNote = '', paidRef = '';
        if (status === 'rejected') { reviewNote = prompt('Reason for rejection:') || ''; if (!reviewNote) return; }
        if (status === 'paid')     { paidRef    = prompt('Payment reference (UTR / cheque):') || ''; }
        await api.post(`/reimbursements/${r._id}/review`, { status, reviewNote, paidRef });
        toast.success(`Marked ${status}`);
        setPicked(null);
        load();
    };

    return (
        <div className="space-y-4">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-2xl font-extrabold">Reimbursements — admin</h1>
                    <p className="text-sm text-ink-500">Review claims with proof attachments.</p>
                </div>
                <select className="ag-input w-44" value={filters.status} onChange={e => setFilters({ status: e.target.value })}>
                    <option value="">All statuses</option>
                    <option>submitted</option><option>approved</option><option>rejected</option><option>paid</option><option>cancelled</option>
                </select>
            </div>

            <div className="ag-card p-0 overflow-x-auto">
                <table className="ag-table">
                    <thead><tr>
                        <th>Submitted</th><th>User</th><th>Title</th><th>Category</th><th>Method</th><th>Amount</th><th>Proof</th><th>Status</th><th></th>
                    </tr></thead>
                    <tbody>
                        {items.map(r => (
                            <tr key={r._id}>
                                <td className="text-xs text-ink-500">{new Date(r.createdAt).toLocaleDateString()}</td>
                                <td className="font-medium">{r.user?.name}<div className="text-xs text-ink-500">{r.user?.email}</div></td>
                                <td>{r.title}</td>
                                <td className="text-xs uppercase tracking-wider text-ink-500">{r.category}</td>
                                <td className="text-xs uppercase tracking-wider text-ink-500">{r.paymentMethod}</td>
                                <td>₹ {r.amountINR.toLocaleString('en-IN')}</td>
                                <td className="flex gap-2 py-2.5">
                                    {r.screenshotUrl && <a href={r.screenshotUrl} target="_blank" rel="noreferrer" title={r.screenshotName || 'Screenshot'} className="text-ink-500 hover:text-brand-teal"><ImageIcon className="h-4 w-4" /></a>}
                                    {r.billUrl && <a href={r.billUrl} target="_blank" rel="noreferrer" title={r.billName || 'Bill'} className="text-ink-500 hover:text-brand-teal"><FileText className="h-4 w-4" /></a>}
                                    {!r.screenshotUrl && !r.billUrl && <span className="text-xs text-ink-500">—</span>}
                                </td>
                                <td><span className={`ag-chip ${STATUS_CHIP[r.status]}`}>{r.status}</span></td>
                                <td>
                                    <div className="flex gap-2 justify-end">
                                        <button className="ag-btn-ghost text-xs" onClick={() => setPicked(r)}><Eye className="h-3.5 w-3.5" /> View</button>
                                        {r.status === 'submitted' && (<>
                                            <button className="ag-btn-primary text-xs" onClick={() => review(r, 'approved')}>Approve</button>
                                            <button className="ag-btn-danger text-xs"  onClick={() => review(r, 'rejected')}>Reject</button>
                                        </>)}
                                        {r.status === 'approved' && (
                                            <button className="ag-btn-primary text-xs" onClick={() => review(r, 'paid')}>Mark paid</button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={9} className="text-center text-ink-500 py-8">No reimbursements.</td></tr>}
                    </tbody>
                </table>
            </div>

            {picked && <DetailDrawer r={picked} onClose={() => setPicked(null)} onReview={review} />}
        </div>
    );
}

function DetailDrawer({ r, onClose, onReview }) {
    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 flex justify-end" onClick={onClose}>
            <div className="bg-white w-full max-w-lg h-full shadow-soft p-6 overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg">{r.title}</h3>
                    <span className={`ag-chip ${STATUS_CHIP[r.status]}`}>{r.status}</span>
                </div>
                <div className="mt-1 text-sm text-ink-500">{r.user?.name} · {new Date(r.createdAt).toLocaleString()}</div>

                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                    <KV k="Amount" v={`₹ ${r.amountINR.toLocaleString('en-IN')}`} />
                    <KV k="Category" v={r.category} />
                    <KV k="Payment method" v={r.paymentMethod} />
                    <KV k="Vendor" v={r.vendor || '—'} />
                    <KV k="Incurred on" v={r.incurredOn ? new Date(r.incurredOn).toLocaleDateString() : '—'} />
                    <KV k="Bank details" v={r.bankDetails || '—'} />
                </div>

                {r.reason && <Section title="Reason">{r.reason}</Section>}
                {r.description && <Section title="Description">{r.description}</Section>}
                {r.notes && <Section title="Notes">{r.notes}</Section>}
                {r.reviewNote && <Section title={`Admin note (${r.status})`}>{r.reviewNote}</Section>}
                {r.paidRef && <Section title="Payment reference">{r.paidRef}</Section>}

                <div className="mt-5">
                    <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-2">Attachments</div>
                    <div className="grid grid-cols-2 gap-3">
                        {r.screenshotUrl ? (
                            <a href={r.screenshotUrl} target="_blank" rel="noreferrer" className="block">
                                <div className="ag-card p-2 hover:ring-2 hover:ring-brand-teal/30">
                                    <img src={r.screenshotUrl} alt="screenshot" className="w-full h-32 object-cover rounded" />
                                    <div className="text-xs mt-2 text-ink-500 truncate">{r.screenshotName || 'screenshot'}</div>
                                </div>
                            </a>
                        ) : <div className="text-xs text-ink-500">No screenshot</div>}
                        {r.billUrl ? (
                            <a href={r.billUrl} target="_blank" rel="noreferrer" className="block">
                                <div className="ag-card p-3 hover:ring-2 hover:ring-brand-teal/30">
                                    <div className="h-32 grid place-items-center text-brand-red"><FileText className="h-12 w-12" /></div>
                                    <div className="text-xs mt-2 text-ink-500 truncate">{r.billName || 'bill'}</div>
                                </div>
                            </a>
                        ) : <div className="text-xs text-ink-500">No bill</div>}
                    </div>
                </div>

                {r.status === 'submitted' && (
                    <div className="mt-6 flex gap-2">
                        <button className="ag-btn-primary flex-1 justify-center" onClick={() => onReview(r, 'approved')}>Approve</button>
                        <button className="ag-btn-danger flex-1 justify-center"  onClick={() => onReview(r, 'rejected')}>Reject</button>
                    </div>
                )}
                {r.status === 'approved' && (
                    <button className="ag-btn-primary w-full justify-center mt-6" onClick={() => onReview(r, 'paid')}>Mark paid</button>
                )}
            </div>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold">{k}</div>
            <div className="text-sm font-medium mt-0.5">{v}</div>
        </div>
    );
}
function Section({ title, children }) {
    return (
        <div className="mt-5">
            <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold">{title}</div>
            <div className="text-sm mt-1 whitespace-pre-wrap">{children}</div>
        </div>
    );
}
